<i18n>
{
	"en": {
		"autoSchedule": "Generate automatically",
		"autoScheduleInfo0": "Orders will be generated 14 days ahead every hour.",
		"autoScheduleInfo1": "Order templates that are connected to a trip template will be generated together with the trip, without regarg to these settings.",
		"generatedByTrips": "Is generated by trip templates: {tripTemplateNames}",
		"noName": "<no name>",
		"yearlyInfo": "Orders will be scheduled on the specified dates every year.",
		"duration": "Duration",
		"days": "Day | Days",
		"frequency": "Frequency",
		"selectFrequency": "Select frequency",
		"startDate": "Start date",
		"endDate": "End date",
		"selectDate": "Select date",
		"selectDays": "Select days",
		"runEvery": "Every",
		"monday": "Monday",
		"tuesday": "Tuesday",
		"wednesday": "Wednesday",
		"thursday": "Thursday",
		"friday": "Friday",
		"saturday": "Saturday",
		"sunday": "Sunday",
		"daily": "Daily",
		"weekly": "Weekly",
		"twoWeekly": "14 days",
		"monthly": "Monthly",
		"specificDates": "Reoccurring dates",
		"oddWeeks": "Odd weeks",
		"evenWeeks": "Even weeks",
		"departure": "Departure time",
		"arrival": "Arrival time",
		"selectTime": "Select time",
		"nextOrder": "Next generation",
		"ordersExist": "Orders may have already been generated from this template. Please remove their connection manually if they should be generated again"
	},
	"no": {
		"name": "Navn",
		"autoSchedule": "Generer automatisk",
		"autoScheduleInfo0": "Oppdrag vil bli generert 14 dager frem i tid hver time.",
		"autoScheduleInfo1": "Oppdragsmaler som er knyttet til turmaler vil uansett bli generert sammen med turen, uavhengig av disse innstillingene.",
		"generatedByTrips": "Blir generert av turmaler: {tripTemplateNames}",
		"noName": "<uten navn>",
		"yearlyInfo": "Oppdrag opprettes på de gitte datoene hvert år.",
		"duration": "Varighet",
		"days": "Dag | Dager",
		"frequency": "Frekvens",
		"selectFrequency": "Velg frekvens",
		"startDate": "Stardato",
		"endDate": "Sluttdato",
		"selectDate": "Velg dato",
		"selectDays": "Velg dager",
		"runEvery": "Hver",
		"monday": "Mandag",
		"tuesday": "Tirsdag",
		"wednesday": "Onsdag",
		"thursday": "Torsdag",
		"friday": "Fredag",
		"saturday": "Lørdag",
		"sunday": "Søndag",
		"daily": "Daglig",
		"weekly": "Ukentlig",
		"twoWeekly": "14 dager",
		"monthly": "Månedlig",
		"specificDates": "Faste datoer",
		"oddWeeks": "Oddetallsuker",
		"evenWeeks": "Partallsuker",
		"departure": "Avgangstid",
		"arrival": "Ankomsttid",
		"selectTime": "Velg tid",
		"nextOrder": "Neste generering",
		"ordersExist": "Oppdrag kan allerede ha blitt generert fra denne malen. Venligst fjern koblingen manuelt dersom disse skal genereres på nytt"
	}
}
</i18n>

<template>
	<div class="auto-schedule">
		<div v-if="templateDetails.tripTemplateBindings.length" class="generated-by-trips-message">
			{{ $t('generatedByTrips', { tripTemplateNames }) }}
		</div>
		<t-form-group rowGap="5px" style="grid-column: 1/5;">
			<t-checkbox
				v-model="templateDetails.autoSchedule"
				:label="$t('autoSchedule')"
			/>
			<div class="info-text">{{ $t('autoScheduleInfo0') }}</div>
			<div class="info-text">{{ $t('autoScheduleInfo1') }}</div>
			<div
				v-if="originalType && type !== originalType && templateDetails.generatedOrders.length"
				class="info-text warning"
			>{{ $t('ordersExist') }}</div>
		</t-form-group>

		<t-form-group :title="$t('frequency')" style="grid-column: 1/2;">
			<t-select
				:items="frequencies"
				:selectedItem="type"
				:disabled="!templateDetails.autoSchedule"
				@select="type = $event"
			>
				<template #displayedValue>
					{{ $t(type) || $t('selectFrequency') }}
				</template>

				<template #optionItemValue="{ item }">
					{{ $t(item) }}
				</template>
			</t-select>
		</t-form-group>
		<t-form-group :title="$t('startDate')" rowGap="5px" style="grid-column: 2/3;">
			<t-date-picker
				:value="displayDate(startDate)"
				:placeholder="$t('selectDate')"
				:minDate="today()"
				:dateFormat="displayDateFormat"
				:disabled="!templateDetails.autoSchedule"
				width="100%"
				@input="startDate = storageDate($event)"
			/>
		</t-form-group>
		<t-form-group :title="$t('endDate')" rowGap="5px" style="grid-column: 3/4;">
			<t-date-picker
				:value="displayDate(endDate)"
				:placeholder="$t('selectDate')"
				:minDate="today()"
				:dateFormat="displayDateFormat"
				:disabled="!templateDetails.autoSchedule"
				width="100%"
				@input="endDate = storageDate($event)"
			/>
		</t-form-group>

		<t-form-group
			v-if="templateDetails.nextOrder"
			:title="$t('nextOrder')"
			rowGap="5px"
			style="grid-column: 4/5;"
		>
			<t-text-input
				:value="displayDate(templateDetails.nextOrder)"
				disabled
			/>
		</t-form-group>

		<!-- Weekly selection -->
		<t-form-group v-if="templateDetails.autoSchedule && type === 'weekly'" rowGap="5px" style="grid-column: 1/5;">
			<div class="select-group">
				<t-checkbox
					v-for="day of days"
					:key="day"
					:checked="weekly[day]"
					:label="$t(day)"
					style="margin-right: 20px;"
					@change="weekly = { day, value: $event }"
				/>
			</div>
		</t-form-group>

		<!-- Two weekly selection -->
		<t-form-group v-if="templateDetails.autoSchedule && type === 'twoWeekly'" rowGap="5px" style="grid-column: 1/5;">
			<div class="select-group">
				<span style="width: 80px; margin-right: 20px;">{{ $t('oddWeeks') }}:</span>
				<t-checkbox
					v-for="day of days"
					:key="day"
					:checked="twoWeekly.oddWeeks[day]"
					:label="$t(day)"
					style="margin-right: 20px;"
					@change="twoWeekly = { week: 'oddWeeks', day, value: $event }"
				/>
			</div>
			<div class="select-group">
				<span style="width: 80px; margin-right: 20px;">{{ $t('evenWeeks') }}:</span>
				<t-checkbox
					v-for="day of days"
					:key="day"
					:checked="twoWeekly.evenWeeks[day]"
					:label="$t(day)"
					style="margin-right: 20px;"
					@change="twoWeekly = { week: 'evenWeeks', day, value: $event }"
				/>
			</div>
		</t-form-group>

		<!-- Monthly scheduling selection -->
		<t-form-group v-if="templateDetails.autoSchedule && type === 'monthly'" rowGap="5px" style="grid-column: 1/5;">
			<div v-for="(value, name) in monthly" :key="name" class="select-group">
				<span>{{ $t('runEvery') }}</span>
				<t-select
					:items="numbers"
					:selectedItem="value.number"
					style="width: 65px;"
					@select="monthly = { id: name, key: 'number', value: $event }"
				>
					<template #displayedValue>
						{{ value.number }}.
					</template>
					<template #optionItemValue="{ item }">
						{{ item }}.
					</template>
				</t-select>
				<t-select
					:items="days"
					:selectedItem="value.day"
					style="width: 200px;"
					@select="monthly = { id: name, key: 'day', value: $event }"
				>
					<template #displayedValue>
						{{ value.day ? $t(value.day) : $t('selectDay') }}
					</template>
					<template #optionItemValue="{ item }">
						{{ $t(item) }}
					</template>
				</t-select>
				<t-icon-button
					v-if="monthly.length > 1"
					icon="minus"
					type="bp"
					size="small"
					iconSize="xs"
					appearance="minimal"
					@click="removeDay(name)"
				/>
			</div>
			<t-icon-button
				icon="plus"
				type="bp"
				size="small"
				iconSize="xs"
				appearance="minimal"
				@click="addDay()"
			/>
		</t-form-group>

		<!-- Specific dates selection -->
		<t-form-group v-if="templateDetails.autoSchedule && type === 'specificDates'" rowGap="5px" style="grid-column: 1/5">
			<span class="info-text" style="margin-bottom: 10px;">{{ $t('yearlyInfo') }}</span>
			<div v-for="(value, name) in specificDates" :key="name" class="select-group">
				<t-date-picker
					:value="value"
					:placeholder="$t('selectDate')"
					width="120px"
					dateFormat="DD.MM"
					headerFormat="MMMM"
					@input="specificDates = { id: name, value: $event }"
				/>
				<t-icon-button
					v-if="specificDates.length > 1"
					icon="minus"
					type="bp"
					size="small"
					iconSize="xs"
					appearance="minimal"
					@click="removeDay(name)"
				/>
			</div>
			<t-icon-button
				icon="plus"
				type="bp"
				size="small"
				iconSize="xs"
				appearance="minimal"
				@click="addDay()"
			/>
		</t-form-group>
	</div>
</template>

<script>
import moment from 'moment';
import { displayDateFormat, storageDateFormat } from '@/core';
import { mapState, mapMutations } from '../store/orderTemplates.store';
export default {
	name: 'ScheduleInterval',
	data() {
		return {
			frequencies: [ 'daily', 'weekly', 'twoWeekly', 'monthly', 'specificDates' ],
			days: [ 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday' ],
			numbers: [ 1, 2, 3, 4 ],
			originalType: '',
			frequency: {}
		};
	},
	computed: {
		...mapState(['templateDetails']),
		tripTemplateNames() {
			return this.templateDetails.tripTemplateBindings.map(
				ttb => ttb.tripTemplate && ttb.tripTemplate.name ? ttb.tripTemplate.name : this.$t('noName'))
				.reduce((res, k, i, arr) => [res, k].join(i  === arr.length - 1 ? ' & ' : ', '));
		},

		type: {
			get() {
				return this.frequency.type;
			},
			set(value) {
				this.frequency.type = value;
			}
		},
		startDate: {
			get() {
				return this.frequency.startDate;
			},
			set(value) {
				this.frequency.startDate = value;
			}
		},
		endDate: {
			get() {
				return this.frequency.endDate;
			},
			set(value) {
				this.frequency.endDate = value;
			}
		},
		weekly: {
			get() {
				return this.frequency.weekly;
			},
			set({ day, value }) {
				this.frequency.weekly[day] = value;
			}
		},
		twoWeekly: {
			get() {
				return this.frequency.twoWeekly;
			},
			set({ week, day, value }) {
				this.frequency.twoWeekly[week][day] = value;
			}
		},
		monthly: {
			get() {
				return this.frequency.monthly;
			},
			set({ id, key, value }) {
				this.frequency.monthly[id][key] = value;
			}
		},
		specificDates: {
			get() {
				return this.frequency.specificDates;
			},
			set({ id, value }) {
				this.frequency.specificDates[id] = value;
			}
		},
		displayDateFormat() {
			return displayDateFormat;
		}
	},
	watch: {
		frequency: {
			deep: true,
			handler(newVal) {
				this.updateTemplateDetails({ key: 'frequency', value: newVal });
			}
		}
	},
	mounted() {
		this.originalType = this.templateDetails.frequency.type;
		this.frequency = JSON.parse(JSON.stringify(this.templateDetails.frequency));
	},
	methods: {
		...mapMutations(['updateTemplateDetails']),
		today() {
			return moment().startOf('day');
		},
		storageDate(displayDate) {
			if (displayDate) {
				return moment(displayDate, displayDateFormat).startOf('day').format(storageDateFormat);
			} else {
				return '';
			}
		},
		displayDate(storageDate) {
			if (storageDate) {
				return moment(storageDate, storageDateFormat).startOf('day').format(displayDateFormat);
			} else {
				return '';
			}
		},
		addDay() {
			let newDay = null;
			if (this.type === 'monthly') {
				newDay = {
					number: 1,
					day: 'monday'
				};
			} else if (this.type === 'specificDates' ) {
				newDay = '';
			}
			if (newDay)
				this.frequency[this.type].push(newDay);
		},
		removeDay(id) {
			let clone = JSON.parse(JSON.stringify(this.frequency[this.type]));
			clone.splice(id, 1);
			this.frequency[this.type] = clone;
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.auto-schedule {
	display: grid;
	grid-template-columns: repeat(4, minmax(min-content, 1fr));
	column-gap: 20px;
	row-gap: 20px;
}

.info-text {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
	user-select: none;

	&.warning {
		color: get-color($text, 'danger')
	}
}

.select-group {
	display: flex;
	flex-direction: row;
	align-items: center;
	> * {
		margin-right: 10px;
	}
}

.generated-by-trips-message {
	grid-column: 1/-1;
}
</style>